import type { CommandResult } from '@travel-planner/shared/schemas';
import type { AgentPanelAdapter } from './types';
import { toastStore } from '@/shared/ui';
import { isPageActionResult } from './page-actions';
import { usePageActionFlashStore } from './use-page-action-flash';

// Generic UX wrapper around an adapter's `onCommandResult` callback for
// page-action-kind results (apply_filters, future search/sort/etc.).
//
// Sequence:
//   1. Snapshot the current URL search string BEFORE the adapter writes.
//   2. Call the adapter's `onCommandResult` so the page mutates URL state.
//   3. After a 400ms delay (so the consultant briefly sees the in-panel
//      summary chip), close the panel.
//   4. Trigger a flash on the page's subscribed root element.
//   5. Show a global toast with the result's humanSummary + an Undo
//      button that restores the snapshotted URL.
//
// If the result is NOT a page-action kind, the adapter callback fires as
// before with no extra UX — preserves today's in-panel renderer flow for
// itinerary search results, etc.
//
// `closePanelDelayMs` is exposed so tests can shorten the delay.

export const PANEL_CLOSE_DELAY_MS = 400;

type Args = {
  result: CommandResult;
  adapter: AgentPanelAdapter | null | undefined;
  closePanel: () => void;
};

export const handlePageActionResult = ({
  result,
  adapter,
  closePanel,
}: Args): void => {
  if (!isPageActionResult(result)) {
    adapter?.onCommandResult?.(result);
    return;
  }

  // Snapshot first — adapter callback below mutates window.location.
  const snapshot = typeof window !== 'undefined' ? window.location.search : '';

  adapter?.onCommandResult?.(result);

  // The in-panel renderer chip (e.g. ApplyFiltersConfirmation) is the
  // single visible confirmation of the result; we no longer mirror the
  // summary into a sticky `lastPageAction` chip above the input —
  // duplicating the same line both above the input and inside the body
  // (and again in the conversation history) was visually noisy. The
  // global Undo toast handles the persistent affordance.

  window.setTimeout(() => closePanel(), PANEL_CLOSE_DELAY_MS);
  usePageActionFlashStore.getState().triggerFlash();

  toastStore.getState().show({
    message: result.humanSummary || 'Filters applied',
    action: {
      label: 'Undo',
      onClick: () => {
        // Restore the snapshotted query string. Using replaceState keeps
        // the browser history clean (no extra back-button entry).
        const target = `${window.location.pathname}${snapshot}${window.location.hash}`;
        window.history.replaceState({}, '', target);
        // Notify the router so it re-validates search params and the page
        // re-runs its loaders. TanStack Router listens to `popstate`.
        window.dispatchEvent(new PopStateEvent('popstate'));
      },
    },
  });
};
